import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  groupFlagsByArea,
  useFeatureFlagRegistry
} from '../../api/useFeatureFlags';

interface Props {
  /** The group's saved opt-ins. A key ABSENT here means "inherit the global". */
  value: Record<string, boolean>;
  onChange: (next: Record<string, boolean>) => void;
  /**
   * The post-chain ON set the backend computed for this group. Used only to
   * annotate what each flag currently RESOLVES to, never to derive it.
   */
  resolved?: string[];
  /**
   * The group's SAVED map, so a row edited since load can drop the `resolved`
   * annotation — which describes the saved state and would otherwise contradict
   * the control the operator just changed.
   */
  savedValue?: Record<string, boolean>;
  /**
   * True while the group is on/panicked, or while its pod state is UNKNOWN.
   * The backend 409s an effective change in that state, because the set is
   * handed to the pod at BOOT.
   */
  locked: boolean;
  /** Why it is locked, shown in the tooltip and the banner. */
  lockReason?: string;
}

/** Tri-state, mirroring the placement-gate selects on the same dialog. */
const flagToInput = (v: boolean | undefined): string =>
  v == null ? '' : v ? 'on' : 'off';

/**
 * The per-group engine feature-flag controls, GENERATED from the registry
 * served by opener-orchestrator. Declaring a new flag needs no change here.
 *
 * Tri-state and not a switch, because the backend chain is
 * `group ?? featureFlagDefaults ?? false` and treats an explicit `false` as an
 * OVERRIDE that deletes an inherited ON — which a binary cannot express. With a
 * switch, a flag turned on globally would render OFF on every group that had not
 * opted in, and toggling one on and back off would silently pin that group off
 * forever, invisible against "never touched".
 *
 * The list is never faked: if the registry cannot be fetched the section says
 * so, because rendering zero controls would read as "this build has no flags".
 */
export function FeatureFlagsSection({
  value,
  onChange,
  resolved,
  savedValue,
  locked,
  lockReason
}: Props) {
  const [open, setOpen] = useState(false);
  const [justCleared, setJustCleared] = useState<string[]>([]);
  const {
    data: registry,
    isLoading,
    isError,
    isFetching,
    isFetched
  } = useFeatureFlagRegistry();
  // "Retired" is inferred from ABSENCE, so it is only safe against a registry
  // fetched during THIS mount. A cached one from before a deploy is missing
  // that deploy's flags, and offering to clear a live opt-in is worse than
  // showing nothing.
  const registryIsFresh = isFetched && !isFetching && !isError;

  const setFlag = (key: string, input: string) => {
    const next = { ...value };
    // "inherit" DELETES the key. Writing `false` would pin the group off and
    // stop it ever picking the flag up when it is promoted globally.
    if (input === '') delete next[key];
    else next[key] = input === 'on';
    onChange(next);
  };

  const knownKeys = new Set((registry ?? []).map(f => f.key));
  // Keys the group still carries from a flag that has since been DELETED from
  // the registry. They cannot be saved (the backend 400s an unknown key) and
  // no control renders for them, so without this row the group can never have
  // any flag changed again — and the header would count an override nobody can
  // see. Only trustworthy once the registry actually loaded.
  const retiredKeys = registryIsFresh
    ? Object.keys(value).filter(k => !knownKeys.has(k))
    : [];
  // Unknown when the registry is not trustworthy — NOT "all of them". Counting
  // every key on the error path is the same "an override nobody can see" defect
  // this guard exists to remove, just on the branch where no control renders.
  const overrideCount = registryIsFresh
    ? Object.keys(value).filter(k => knownKeys.has(k)).length
    : null;

  return (
    <Box>
      <Stack
        component="button"
        type="button"
        direction="row"
        alignItems="center"
        spacing={0.5}
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        sx={{
          cursor: 'pointer',
          userSelect: 'none',
          background: 'none',
          border: 'none',
          p: 0,
          color: 'inherit',
          font: 'inherit',
          width: '100%'
        }}
      >
        {open ? (
          <ExpandMoreIcon fontSize="small" />
        ) : (
          <ChevronRightIcon fontSize="small" />
        )}
        <Typography
          component="span"
          sx={{ fontSize: '0.8rem', fontWeight: 600 }}
        >
          Feature flags
        </Typography>
        {overrideCount != null && overrideCount > 0 && (
          <Tooltip title={`${overrideCount} group override(s)`}>
            <Chip
              component="span"
              label={overrideCount}
              size="small"
              color="info"
              sx={{ fontSize: '0.6rem', height: 16 }}
            />
          </Tooltip>
        )}
      </Stack>

      <Collapse in={open} unmountOnExit>
        <Box sx={{ pl: 3, pt: 1 }}>
          {locked && (
            <Alert severity="info" sx={{ mb: 1, py: 0, fontSize: '0.72rem' }}>
              {lockReason ??
                'Stop the group to change flags — the set is delivered to the engine pod at boot.'}
            </Alert>
          )}

          {isLoading && (
            <Typography sx={{ fontSize: '0.72rem', color: 'grey.500' }}>
              Loading the registry…
            </Typography>
          )}

          {isError && (
            <Alert severity="warning" sx={{ py: 0, fontSize: '0.72rem' }}>
              Could not load the feature-flag registry. No controls are shown —
              this is NOT the same as "this build has no flags".
            </Alert>
          )}

          {retiredKeys.length > 0 && (
            <Alert
              severity="warning"
              sx={{ mb: 1, py: 0, fontSize: '0.72rem' }}
              action={
                <Button
                  size="small"
                  variant="outlined"
                  disabled={locked}
                  onClick={() => {
                    const next = { ...value };
                    for (const k of retiredKeys) delete next[k];
                    onChange(next);
                    setJustCleared(retiredKeys);
                  }}
                >
                  Clear
                </Button>
              }
            >
              This group carries {retiredKeys.length} key(s) from deleted
              flag(s): {retiredKeys.join(', ')}. They do nothing, but the backend
              rejects any save that still includes them.
            </Alert>
          )}

          {retiredKeys.length === 0 && justCleared.length > 0 && (
            // The warning Alert unmounts the moment the keys are gone, taking
            // the focused button with it. This replaces it in place so a
            // keyboard operator is told what happened instead of losing focus
            // into an empty region.
            <Alert severity="success" sx={{ mb: 1, py: 0, fontSize: '0.72rem' }}>
              Cleared {justCleared.join(', ')} — save to apply.
            </Alert>
          )}

          {registry && registry.length === 0 && !isError && (
            <Typography sx={{ fontSize: '0.72rem', color: 'grey.500' }}>
              No flags are declared in this build.
            </Typography>
          )}

          {registry &&
            groupFlagsByArea(registry).map(([area, flags]) => (
              <Box key={area} sx={{ mb: 1.5 }}>
                <Typography
                  sx={{
                    fontSize: '0.65rem',
                    textTransform: 'uppercase',
                    color: 'grey.500',
                    letterSpacing: 0.5
                  }}
                >
                  {area}
                </Typography>
                {flags.map(flag => {
                  const resolvesOn = resolved?.includes(flag.key);
                  // The annotation reflects the SAVED map. Once the operator
                  // edits this row it is stale, and "resolves off" next to a
                  // control the operator just set to On reads as "that did
                  // nothing".
                  const edited = value[flag.key] !== savedValue?.[flag.key];
                  return (
                    <Box key={flag.key} sx={{ mt: 1 }}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.5}
                        sx={{ mb: 0.25 }}
                      >
                        <Typography sx={{ fontSize: '0.75rem' }}>
                          {flag.key}
                        </Typography>
                        {flag.status === 'experimental' && (
                          <Chip
                            label="experimental"
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.55rem', height: 14 }}
                          />
                        )}
                        <Tooltip title={`Retires on ${flag.retireBy}`}>
                          <Chip
                            label={`↧ ${flag.retireBy}`}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.55rem', height: 14 }}
                          />
                        </Tooltip>
                        {resolved && !edited && (
                          <Chip
                            label={
                              resolvesOn ? 'saved: resolves ON' : 'saved: resolves off'
                            }
                            size="small"
                            variant="outlined"
                            color={resolvesOn ? 'success' : 'default'}
                            sx={{ fontSize: '0.55rem', height: 14 }}
                          />
                        )}
                      </Stack>
                      <Tooltip
                        title={
                          locked
                            ? (lockReason ?? 'Stop the group to change flags')
                            : flag.description
                        }
                      >
                        {/* MUI drops tooltips on disabled controls — the span
                            is what keeps the explanation reachable. */}
                        <span>
                          <TextField
                            select
                            size="small"
                            disabled={locked}
                            // displayEmpty + shrink, or MUI renders the ''
                            // (inherit) case as a BLANK box: it only computes a
                            // display value when the value is "filled". Inherit
                            // is the most common state, and blank reads as
                            // "nothing loaded", which is the ambiguity the
                            // tri-state exists to remove.
                            SelectProps={{
                              displayEmpty: true,
                              inputProps: { 'aria-label': flag.key }
                            }}
                            InputLabelProps={{ shrink: true }}
                            // The LABEL has to be the flag key: MUI emits both
                            // aria-labelledby (from the label) and aria-label,
                            // and labelledby wins — a label of "State" would
                            // give every row in the dialog the same accessible
                            // name.
                            label={flag.key}
                            value={flagToInput(value[flag.key])}
                            onChange={e => setFlag(flag.key, e.target.value)}
                            sx={{ minWidth: 220 }}
                          >
                            <MenuItem value="">— inherit (global) —</MenuItem>
                            <MenuItem value="on">On</MenuItem>
                            <MenuItem value="off">Off (override)</MenuItem>
                          </TextField>
                        </span>
                      </Tooltip>
                      <Typography
                        sx={{
                          fontSize: '0.68rem',
                          color: 'grey.500',
                          mt: 0.25
                        }}
                      >
                        {flag.description}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            ))}
        </Box>
      </Collapse>
    </Box>
  );
}

export default FeatureFlagsSection;
